import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form, Field } from 'formik'

import { useModifierFormHelpers, useHandleSubmit } from '../use-modifier-form-helpers'
import { useCart } from '../../CartProvider/CartProvider'
import Modal from '../../Modal/Modal'
import { ModifierBody } from '../ModifierBody/ModifierBody'
import { ItemQuantity } from '../ItemQuantity/ItemQuantity'
import { FractionalItemQuantity } from '../FractionalItemQuantity/FractionalItemQuantity'

import { ModifiersFooter, LoadingModal, ErrorModal } from '../ModifiersModalHelpers'
import { RemoveItemButton } from '../RemoveItemButton/RemoveItemButton'
import { useAvailability } from '../../../utils/availability'
import { useRestaurant } from '../../RestaurantProvider/RestaurantProvider'

export const UpdateItemFormModal = ({
  updateCart,
  error,
  itemDetailsQuery,
  getItemDetails,
  getBasePrice,
  onClose,
  itemGuid,
  itemGroupGuid,
  selectionGuid,
  mayDelete,
  isUpsell = false
}) => {
  const { cartGuid } = useCart()
  const { restaurantInfo } = useRestaurant()

  const [modalError, setError] = useState(null)
  const [modalLoading, setLoading] = useState(false)

  const itemDetails = getItemDetails(itemDetailsQuery.data)
  const basePrice = getBasePrice(itemDetailsQuery.data)

  const restaurant = restaurantInfo.data && restaurantInfo.data.restaurant

  const { loading: availabilityLoading, orderingAvailable } = useAvailability()

  const {
    usesFractionalQuantity,
    fractionalQuantity,
    unitOfMeasure
  } = itemDetails || {}

  const {
    getComputedPrice,
    getModifierGroups,
    formikProps
  } = useModifierFormHelpers({
    itemDetails,
    itemGroupGuid,
    usesFractionalQuantity,
    fractionalQuantity
  })

  const handleSubmit = useHandleSubmit({
    itemDetails,
    restaurant,
    getComputedPrice,
    basePrice,
    cartGuid,
    itemGuid,
    itemGroupGuid,
    getModifierGroups,
    updateCart,
    orderingAvailable,
    selectionGuid
  })

  const getDisplayPrice = useCallback((values) => {
    if (usesFractionalQuantity) {
      const fractionalBasePrice = basePrice * values.fractionalQuantity
      const modifierPrice = getComputedPrice(values, values.quantity)

      return fractionalBasePrice + modifierPrice
    }

    return (getComputedPrice(values, values.quantity) + basePrice) * values.quantity
  }, [basePrice, getComputedPrice, usesFractionalQuantity])

  if (itemDetailsQuery.loading || !itemDetailsQuery.called || restaurantInfo.loading) {
    return <LoadingModal onClose={onClose} />
  }
  if (itemDetailsQuery.error || restaurantInfo.error) {
    return (
      <ErrorModal
        onClose={onClose}
        error={itemDetailsQuery.error}
        retry={itemDetailsQuery.refetch}
      />
    )
  }

  return (
    <Formik
      {...formikProps}
      onSubmit={handleSubmit}
    >
      {({ values, isValid, isSubmitting }) => (
        <Modal
          header={itemDetails.name}
          error={error || modalError}
          wrapper={children => <Form>{children}</Form>}
          onClose={onClose}
          footer={<ModifiersFooter
            loading={isSubmitting || modalLoading || availabilityLoading}
            isValid={isValid}
            quantity={usesFractionalQuantity ? 1 : values.quantity}
            price={getDisplayPrice(values)}
            onClose={onClose}
            addToCartEnabled={orderingAvailable}
            additionalText={isUpsell ? ' And Checkout' : ''}
          />}
        >
          <ModifierBody
            itemDetails={itemDetails}
            values={values}
            specialRequestsConfig={restaurant.specialRequestsConfig}
            quantityInputField={usesFractionalQuantity ? (
              <Field
                name='fractionalQuantity'
                component={FractionalItemQuantity}
                unitOfMeasure={unitOfMeasure}
                price={basePrice}
              />
            ) : (
              <Field
                name='quantity'
                component={ItemQuantity}
              />
            )}
            deleteButton={mayDelete && (
              <RemoveItemButton
                updateError={setError}
                updateLoading={setLoading}
                handleDeleteParams={{
                  cartGuid,
                  itemGuid,
                  selectionGuid,
                  getComputedPrice,
                  basePrice,
                  itemDetails
                }}
                values={values}
                loading={modalLoading || isSubmitting || availabilityLoading}
              />
            )}
          />
        </Modal>
      )}
    </Formik>
  )
}

const itemDetailsQueryShape = PropTypes.shape({
  loading: PropTypes.bool.isRequired,
  refetch: PropTypes.func,
  data: PropTypes.object,
  error: PropTypes.object
})

UpdateItemFormModal.propTypes = {
  updateCart: PropTypes.func.isRequired,
  error: PropTypes.object,
  itemDetailsQuery: itemDetailsQueryShape.isRequired,
  getItemDetails: PropTypes.func.isRequired,
  getBasePrice: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  itemGuid: PropTypes.string.isRequired,
  itemGroupGuid: PropTypes.string.isRequired,
  // for editing
  selectionGuid: PropTypes.string,
  mayDelete: PropTypes.bool,
  isUpsell: PropTypes.bool
}
